import { useCallback, useState } from 'react';
import { FilterBlockOptions } from './FilterBlock';

interface UseFilterProps {
  initialFilters: FilterBlockOptions;
  onChange?: (filters: FilterBlockOptions) => void;
  onRemove?: (filters: FilterBlockOptions) => void;
  onApply?: (filters: FilterBlockOptions) => void | Promise<void>;
}

const useFilter = ({
  initialFilters,
  onChange,
  onRemove,
  onApply,
}: UseFilterProps) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<FilterBlockOptions>(initialFilters);
  const [selectedTags, setSelectedTags] = useState<FilterBlockOptions>(initialFilters);

  const handleRemove = useCallback((category: string, tag: string) => {
    setSelectedTags(prev => {
      const updated = { ...prev, [category]: prev[category].filter(o => o !== tag) };
      onRemove?.(updated);
      return updated;
    });
  }, [onRemove]);

  const handleRemoveAll = useCallback(() => {
    const empty = Object.fromEntries(Object.keys(selectedTags).map(k => [k, []]));
    onRemove?.(empty);
    setSelectedTags(empty);
  }, [onRemove, selectedTags]);

  const handleClearFilters = useCallback(() => {
    const empty = Object.fromEntries(Object.keys(selectedOptions).map(k => [k, []]));
    setSelectedOptions(empty);
  }, [selectedOptions]);

  const handleSelect = useCallback((category: string, selectedValues: string[]) => {
    setSelectedOptions(prev => ({ ...prev, [category]: selectedValues }));
    onChange?.({ ...selectedOptions, [category]: selectedValues });
  }, [onChange, selectedOptions]);

  const handleApply = useCallback(async () => {
    setSelectedTags(selectedOptions);
    await Promise.resolve(onApply?.(selectedOptions));
    setDropdownVisible(false);
  }, [onApply, selectedOptions]);

  const toggleDropdown = useCallback(() => {
    if (dropdownVisible) {
      setDropdownVisible(false);
      setSelectedOptions(initialFilters);
      return;
    }
    setDropdownVisible(true);
  }, [dropdownVisible, initialFilters]);

  return {
    selectedTags,
    handleSelect,
    handleRemove,
    handleRemoveAll,
    handleApply,
    handleClearFilters,
    selectedOptions,
    toggleDropdown,
    dropdownVisible,
  };
};

export default useFilter;